import { createApi } from '@reduxjs/toolkit/query/react';
import { endpoints } from 'config/api';
import { axiosBaseQueryFactory } from 'config/axiosBaseQuery';
import { CustomerLoginResponse, ResendVerificationResponse, VerifyEmailResponse } from './authTypes';
import { setAuthUser, updateAuthUser } from './authSlice';

export const authApi = createApi({
  reducerPath: 'authApi',
  baseQuery: axiosBaseQueryFactory({ isPublic: true }),
  endpoints: builder => ({
    login: builder.mutation({
      query: credentials => ({
        url: endpoints.auth.login,
        method: 'POST',
        data: credentials,
      }),
      async onQueryStarted(arg, {queryFulfilled, dispatch}) {
        try {
          const {data} = await queryFulfilled;
          // Use Redux slice instead of localStorage directly
          dispatch(setAuthUser(data));
        } catch (e) {
          console.error('Login failed:', e);
        }
      },
    }),

    customerLogin: builder.mutation<CustomerLoginResponse, any>({
      query: credentials => ({
        url: endpoints.customers.login,
        method: 'POST',
        data: credentials,
      }),
      async onQueryStarted(arg, {queryFulfilled, dispatch}) {
        try {
          const {data} = await queryFulfilled;
          // Use Redux slice instead of localStorage directly
          dispatch(setAuthUser(data));
        } catch (e) {
          console.error('Login failed:', e);
        }
      },
    }),

    forgotPassword: builder.mutation({
      query: email => ({
        url: endpoints.auth.forgot,
        method: 'POST',
        data: { email },
      }),
    }),

    resetPassword: builder.mutation({
      query: ({ token, password }) => ({
        url: endpoints.auth.reset,
        method: 'POST',
        data: { token, newPassword: password },
      }),
    }),

    changePassword: builder.mutation({
      query: ({currentPassword, newPassword}) => ({
        url: endpoints.auth.change,
        method: 'PUT',
        data: {currentPassword, newPassword},
        isPublic: false,
      }),
    }),

    verifyEmail: builder.query<VerifyEmailResponse, string>({
      query: (token: string) => ({
        url: endpoints.auth.verifyEmail(token),
        method: 'GET',
      }),
      async onQueryStarted(_arg, { queryFulfilled, dispatch, getState }) {
        try {
          const { data } = await queryFulfilled;
          if (data?.success) {
            const state = getState() as unknown as { auth: { user: unknown } };
            if (state.auth.user) {
              dispatch(updateAuthUser({ isEmailVerified: true }));
            }
          }
        } catch {
          // Verification failed — handled by the component
        }
      },
    }),

    getMe: builder.query<{ user: Record<string, unknown> }, void>({
      query: () => ({
        url: endpoints.auth.me,
        method: 'GET',
        isPublic: false,
      }),
      async onQueryStarted(_arg, { queryFulfilled, dispatch }) {
        try {
          const { data } = await queryFulfilled;
          if (data?.user) {
            dispatch(updateAuthUser(data.user));
          }
        } catch {
          // Silent fail — stale localStorage data remains as fallback
        }
      },
    }),

    resendVerification: builder.mutation<ResendVerificationResponse, void>({
      query: () => ({
        url: endpoints.auth.resendVerification,
        method: 'POST',
        isPublic: false,
      }),
    }),
  }),
});

export const {
  useLoginMutation,
  useForgotPasswordMutation,
  useResetPasswordMutation,
  useChangePasswordMutation,
  useCustomerLoginMutation,
  useLazyVerifyEmailQuery,
  useResendVerificationMutation,
  useGetMeQuery,
} = authApi;

export const useLoginFactory = (isCustomerLogin: boolean) =>
  isCustomerLogin ? useCustomerLoginMutation : useLoginMutation;
